import { useEffect, useRef } from 'react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import type { SpGroupType } from '@/utils/hooks/swr/group/useSPGroups';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import type useIsGroup from '@/utils/hooks/user/useIsGroup';
import { useGetFunnelCreationStatus } from '@/utils/scheduler/funnel/hooks/useGetFunnelCreationStatus';
import { useGetSchedulerFunnelProfilesId } from '@/utils/scheduler/funnel/hooks/useGetSchedulerFunnelProfilesId';

import { mixPanelSchedulerNewPost } from '@/utils/mixpanel/schedulerEvents';
import useRouterQuery from '@/utils/hooks/utils/useRouterQuery';
import useToggle from '@/utils/hooks/utils/useToggle';

import type { SocialProfileType } from '@/types/socialProfileType';

import { tabsList } from '@/lib/scheduler/tabs';

import Icon from '@/components/base/Icon';
import { ButtonAndDropdown } from '@/components/composite/Button/ButtonAndDropdown';

const SchedulerFunnel = dynamic(() =>
    import('@/components/composite/Scheduler/Funnel').then((mod) => mod.SchedulerFunnel)
);

type FunnelSettingsType = React.ComponentProps<typeof SchedulerFunnel>['settings'];

type ButtonFunnelModalProps = {
    isGroup: ReturnType<typeof useIsGroup>;
    activeGroup: SpGroupType;
    activeSocialProfile: SocialProfileType;
    setNeedReloadScheduledPosts: React.Dispatch<React.SetStateAction<boolean>>;
};

export const ButtonFunnelModal = ({
    isGroup,
    activeGroup,
    activeSocialProfile,
    setNeedReloadScheduledPosts
}: ButtonFunnelModalProps) => {
    const router = useRouter();
    const { post_id, social_profile_id, library_media_id, master_display_profile_id } =
        router.query;
    const { t } = useTranslation('scheduler');
    const { deleteAllQueries } = useRouterQuery();

    const {
        isOpen: isOpenFunnel,
        toggle: toggleFunnel,
        params: settings,
        setParams: setSettings
    } = useToggle<FunnelSettingsType>();

    // Ref to track if we're in the process of opening a new modal to avoid race condition
    const isOpening = useRef(false);

    const { profilesId, profilesIdWithPermission } = useGetSchedulerFunnelProfilesId({
        activeGroup,
        activeSocialProfile
    });

    const { creation_type, profiles } = useGetFunnelCreationStatus({
        activeGroup,
        activeSocialProfile,
        isGroup
    });

    /** Utility to safely open the funnel */
    const openFunnel = (newSettings: FunnelSettingsType, shouldClearQuery = true) => {
        if (isOpening.current) return;
        isOpening.current = true;

        setSettings(newSettings);
        toggleFunnel();

        if (shouldClearQuery) {
            router.replace(deleteAllQueries());
        }

        setTimeout(() => (isOpening.current = false), 100);
    };

    /** Automatically open modal based on query params */
    useEffect(() => {
        if (isOpenFunnel || isOpening.current) return;

        if (post_id) {
            openFunnel({
                type: 'edit',
                postId: Number(post_id),
                socialProfileId:
                    Number(social_profile_id) ?? activeSocialProfile?.id ?? profilesId[0],
                masterDisplayProfileId: master_display_profile_id
                    ? Number(master_display_profile_id)
                    : null
            });
        } else if (library_media_id) {
            openFunnel({
                type: creation_type,
                socialProfiles: profiles,
                libraryMediaId: Number(library_media_id)
            });
        }
    }, [post_id, library_media_id]);

    /** Clear settings once the modal is fully closed */
    useEffect(() => {
        if (isOpenFunnel || isOpening.current) return;
        const timeout = setTimeout(() => {
            if (!isOpenFunnel && !isOpening.current) setSettings(null);
        }, 500);
        return () => clearTimeout(timeout);
         
    }, [isOpenFunnel]);

    /** Button handlers */
    const handleNewPost = () => {
        // Launch mix panel event
        const isFromFeed = router.pathname.includes(tabsList[1]?.url);
        const event = isFromFeed ? 'main_cta_feed' : 'main_cta_calendar';
        mixPanelSchedulerNewPost(event);

        // Initialize the funnel with the allowed profiles
        openFunnel({ type: 'create', socialProfiles: profilesIdWithPermission }, false);
    };

    const handleNewReminder = () => {
        openFunnel({ type: 'create_reminder', socialProfiles: profilesId }, false);
    };

    return (
        <>
            <ButtonAndDropdown variant="primary" disableVariant="light">
                <ButtonAndDropdown.MainButton
                    id="schedule-new-post-button"
                    onClick={handleNewPost}
                    disabled={!profilesIdWithPermission.length}
                    data-testid="schedule-new-post-button"
                    className="w-full space-x-2.5">
                    <Icon icon="plus" />
                    <span>{t('schedule_new_post')}</span>
                </ButtonAndDropdown.MainButton>

                <ButtonAndDropdown.DropdownWrapper
                    dropdownPosition="base"
                    button={
                        <ButtonAndDropdown.DropdownButton
                            id="schedule-new-post-button-dropdown"
                            data-testid="dropdown-button">
                            <Icon icon="chevron-bottom" />
                        </ButtonAndDropdown.DropdownButton>
                    }>
                    <div className="flex flex-col space-y-0.5 rounded border border-icoGray-300 bg-white p-0.5 dark:border-icoDarkMode-beton dark:bg-icoDarkMode-deepBlack">
                        <ButtonAndDropdown.DropdownItem className="rounded">
                            {() => (
                                <button
                                    data-testid="schedule-new-reminder-button"
                                    className="flex w-full items-center gap-2 rounded-m p-2 hover:bg-icoBlue-100 hover:text-icoBlue dark:hover:bg-icoBlue/20"
                                    onClick={handleNewReminder}>
                                    <Icon icon="soon" />
                                    <span>{t('scheduler:create_reminder_new_post')}</span>
                                </button>
                            )}
                        </ButtonAndDropdown.DropdownItem>
                    </div>
                </ButtonAndDropdown.DropdownWrapper>
            </ButtonAndDropdown>

            <SchedulerFunnel
                isOpen={isOpenFunnel}
                settings={settings}
                toggle={toggleFunnel}
                callbackSuccess={() => setNeedReloadScheduledPosts(true)}
            />
        </>
    );
};
